import { useEffect, useState } from 'react';
import api from 'utils/api';
import { formToDraftOfferDocument } from './draftOfferApi';

export function budgetEstimateRequest(businessId, values) {
  if (!businessId || !values?.audience?.type || !(Number(values?.budget?.maxAmount) > 0)
    || ![1, 2, 3].includes(Number(values?.budget?.amountPeriod))) return null;
  try {
    const { location, budget } = formToDraftOfferDocument({ values, businessId }).payload.offer;
    return { businessId, location, budget: { maxAmount: budget.maxAmount, amountPeriod: budget.amountPeriod } };
  } catch (_) {
    return null;
  }
}

export async function fetchBudgetEstimate(businessId, values) {
  const request = budgetEstimateRequest(businessId, values);
  if (!request) return null;
  const estimate = await api.post('/drafts/budget-estimate', request);
  return estimate?.pricePerClaim > 0 && estimate?.claims != null ? estimate : null;
}

export async function needsClaimBudgetReview(businessId, values, confirmedValues) {
  const [estimate, confirmed] = await Promise.all([
    fetchBudgetEstimate(businessId, values).catch(() => null),
    fetchBudgetEstimate(businessId, confirmedValues).catch(() => null),
  ]);
  return Boolean(estimate && !sameBudgetBasis(estimate, confirmed));
}

export function sameBudgetBasis(left, right) {
  return Boolean(left && right && Number(left.maxAmount) === Number(right.maxAmount)
    && left.amountPeriod === right.amountPeriod && Number(left.pricePerClaim) === Number(right.pricePerClaim));
}

export function formatBudgetEstimate(estimate) {
  if (!estimate || estimate.claims == null) return '';
  const period = { DAILY: ['daily', 'claims/day'], WEEKLY: ['weekly', 'claims/week'], TOTAL: ['total', 'claims total'] }[estimate.amountPeriod];
  if (!period) return '';
  return `$${Number(estimate.maxAmount)} ${period[0]} @ $${Number(estimate.pricePerClaim).toFixed(2)}/claim = ${estimate.claims} ${period[1]}`;
}

export function useBudgetEstimate(businessId, values) {
  const key = JSON.stringify(budgetEstimateRequest(businessId, values));
  const [result, setResult] = useState(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const reload = () => setRefresh((value) => value + 1);
    window.addEventListener('focus', reload);
    return () => window.removeEventListener('focus', reload);
  }, []);
  useEffect(() => {
    let current = true;
    setResult(null);
    if (key === 'null') return undefined;
    const timer = setTimeout(async () => {
      try {
        const estimate = await api.post('/drafts/budget-estimate', JSON.parse(key));
        if (current) setResult({ key, refresh, estimate: estimate?.pricePerClaim > 0 ? estimate : null });
      } catch (_) {
        if (current) setResult({ key, refresh, estimate: null });
      }
    }, 200);
    return () => { current = false; clearTimeout(timer); };
  }, [key, refresh]);
  // Input changes and focus refreshes hide the old price until the request completes.
  return result?.key === key && result.refresh === refresh ? result.estimate : null;
}
